import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowLeft, MapPin, Check, Search, CalendarDays, Clock, ExternalLink,
  BookOpen, Compass, GraduationCap, FileCheck, Stamp, ShieldCheck, Luggage, ListChecks,
  Globe2, BadgeCheck, Info, ChevronRight,
} from "lucide-react";
import { Reveal, SectionHead } from "@/components/site/Widgets";
import { useMeta } from "@/pages/SeoPages";
import { api, track, WHATSAPP_URL, scrollToSection } from "@/lib/api";

/* ---------------- shared helpers ---------------- */

function useCollection(entity) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get(`/content/${entity}`).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, [entity]);
  return [items, loaded];
}

export function Breadcrumbs({ items }) {
  return (
    <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-6 flex-wrap" data-testid="breadcrumbs">
      {items.map(([label, to], i) => (
        <span key={label} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight className="w-3 h-3" />}
          {to ? <Link to={to} className="hover:text-[#1E40AF]">{label}</Link> : <span className="text-slate-600 font-semibold">{label}</span>}
        </span>
      ))}
    </nav>
  );
}

export function ContentBlocks({ text }) {
  if (!text) return null;
  return text.split("\n\n").map((b, i) =>
    b.startsWith("## ") ? (
      <h2 key={i} className="text-xl sm:text-2xl font-extrabold text-[#0B192C] tracking-tight mt-10 mb-4">{b.slice(3)}</h2>
    ) : (
      <p key={i} className="text-slate-600 leading-relaxed mb-5">{b}</p>
    )
  );
}

export function Disclaimer() {
  return (
    <div className="flex gap-3 bg-[#FEF3C7] border border-[#FDE68A] rounded-xl p-5 mt-10" data-testid="info-disclaimer">
      <Info className="w-5 h-5 text-[#B45309] shrink-0 mt-0.5" />
      <p className="text-xs sm:text-sm text-[#92400E] leading-relaxed">
        Requirements, fees, intakes, visa rules and university policies can change. Students should verify current
        information with the relevant university and official government/immigration sources.
      </p>
    </div>
  );
}

export function CTABanner({ title = "Not sure where to start?", sub = "Talk to Harwell Global Education — a counsellor will help you understand your options." }) {
  const navigate = useNavigate();
  return (
    <Reveal className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-10 text-center mt-14">
      <h3 className="text-xl sm:text-2xl font-extrabold text-[#0B192C] tracking-tight">{title}</h3>
      <p className="text-slate-500 text-sm mt-2 max-w-lg mx-auto">{sub}</p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <button onClick={() => navigate("/contact")} data-testid="cta-banner-counselling"
          className="px-7 py-3.5 rounded-full text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] active:scale-[0.98] transition-all">
          BOOK FREE COUNSELLING
        </button>
        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { source: "cta_banner" })}
          data-testid="cta-banner-whatsapp"
          className="px-7 py-3.5 rounded-full text-sm font-bold text-white bg-[#059669] hover:bg-[#047857] active:scale-[0.98] transition-all">
          WHATSAPP US
        </a>
      </div>
    </Reveal>
  );
}

function LightHero({ overline, title, accent, sub, children, image }) {
  return (
    <section className="bg-[#F8FAFC] mesh-gradient border-b border-slate-200 pt-36 pb-16 lg:pt-44 lg:pb-20">
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${image ? "grid lg:grid-cols-2 gap-12 items-center" : ""}`}>
        <div>
          {overline && <p className="text-xs font-data uppercase tracking-[0.25em] text-[#1E40AF] font-semibold mb-4">{overline}</p>}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0B192C] leading-[1.08] max-w-4xl">
            {title} {accent && <span className="font-editorial italic font-semibold text-[#B45309]">{accent}</span>}
          </h1>
          {sub && <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">{sub}</p>}
          {children}
        </div>
        {image && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10 ring-1 ring-slate-200 h-[240px] lg:h-[320px]">
            <img src={image} alt={title} className="w-full h-full object-cover" />
          </motion.div>
        )}
      </div>
    </section>
  );
}

export function countryHref(c) {
  return c.slug === "romania" ? "/study-in-romania" : `/study-in-${c.slug}`;
}

export function CountryCard({ c, i = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (i % 3) * 0.08 }} whileHover={{ y: -6 }}>
      <Link to={countryHref(c)} data-testid={`country-card-${c.slug}`}
        onClick={() => track("country_enquiry", { country: c.name })}
        className="block bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl hover:border-[#1E40AF]/40 transition-all duration-300 group h-full">
        <div className="relative h-44 overflow-hidden bg-[#EEF5FF]">
          {c.image ? (
            <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-5xl">{c.flag}</div>
          )}
          <span className="absolute bottom-3 left-4 bg-white/90 backdrop-blur text-lg leading-none px-2.5 py-1.5 rounded-lg shadow">{c.flag}</span>
        </div>
        <div className="p-6">
          <h3 className="text-lg font-extrabold text-[#0B192C] uppercase tracking-wide group-hover:text-[#1E40AF] transition-colors">{c.name}</h3>
          <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{c.tagline}</p>
          <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#1E40AF]">
            EXPLORE <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

/* ---------------- DESTINATIONS ---------------- */

export function DestinationsPage() {
  useMeta("Study Destinations — UK, Germany, Italy, Romania, France, Ireland | Harwell Global Education",
    "Explore European and international study destinations with Harwell Global Education: the UK, Germany, Italy, Romania, France and Ireland.");
  const [countries] = useCollection("countries");
  return (
    <div data-testid="destinations-page">
      <LightHero overline="Study Destinations" title="WHERE DO YOU WANT TO" accent="STUDY?"
        sub="Explore countries, courses and universities that align with your academic background, interests and long-term goals." />
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {countries.map((c, i) => <CountryCard key={c.id} c={c} i={i} />)}
          </div>
          <Disclaimer />
          <CTABanner />
        </div>
      </section>
    </div>
  );
}

/* ---------------- COUNTRY PAGE ---------------- */

export function CountryPage({ cslug }) {
  const [countries, loaded] = useCollection("countries");
  const [courses] = useCollection("courses");
  const [unis] = useCollection("universities");
  const [faqs] = useCollection("faqs");
  const country = countries.find((c) => c.slug === cslug);

  useMeta(
    country ? `Study in ${country.name} — Courses, Universities, Costs & Visa | Harwell Global Education` : "Study Destinations | Harwell Global Education",
    country ? `Study in ${country.name}: popular courses, universities, admission considerations, tuition, living costs, scholarships and visa overview — guided by Harwell Global Education.` : ""
  );

  const countryFaqs = useMemo(() => {
    if (!country) return [];
    const kws = [country.name, "IELTS", "cost", "documents", "visa", "requirements"];
    return faqs.filter((f) => kws.some((k) => f.question.toLowerCase().includes(k.toLowerCase()))).slice(0, 6);
  }, [faqs, country]);

  useEffect(() => {
    if (!countryFaqs.length || !country) return;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: countryFaqs.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })),
    });
    document.head.appendChild(s);
    return () => s.remove();
  }, [countryFaqs, country]);

  if (loaded && !country) {
    return <div className="pt-44 pb-32 text-center"><h1 className="text-2xl font-extrabold text-[#0B192C]">Destination not found</h1>
      <Link to="/destinations" className="mt-4 inline-block text-sm font-bold text-[#1E40AF]">All destinations</Link></div>;
  }
  if (!country) return <div className="pt-44 pb-32 text-center text-slate-400">Loading...</div>;

  const relatedCourses = courses.filter((c) => (c.countries || "").toLowerCase().includes(country.name.toLowerCase())).slice(0, 6);
  const relatedUnis = unis.filter((u) => (u.country || "").toLowerCase() === country.name.toLowerCase()).slice(0, 6);

  return (
    <div data-testid={`country-page-${cslug}`}>
      <LightHero overline="Study Destination" title={`STUDY IN`} accent={` ${country.name.toUpperCase()} ${country.flag}`} sub={country.tagline} image={country.image}>
        <div className="mt-7"><Breadcrumbs items={[["Home", "/"], ["Destinations", "/destinations"], [country.name]]} /></div>
      </LightHero>

      <section className="py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <ContentBlocks text={country.content} />
          {country.popular_courses && (
            <>
              <h2 className="text-xl sm:text-2xl font-extrabold text-[#0B192C] tracking-tight mt-10 mb-4">Popular study areas</h2>
              <div className="flex flex-wrap gap-2.5 mb-6">
                {country.popular_courses.split(",").map((p) => (
                  <span key={p} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-700">{p.trim()}</span>
                ))}
              </div>
            </>
          )}
          <Disclaimer />
        </div>
      </section>

      {relatedCourses.length > 0 && (
        <section className="py-14 bg-[#F8FAFC] border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHead overline="Related Courses" title={`COURSES STUDENTS EXPLORE IN ${country.name.toUpperCase()}`} />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedCourses.map((c) => (
                <Link key={c.id} to={`/courses/${c.slug}`} data-testid={`country-course-${c.slug}`}
                  className="bg-white rounded-xl border border-slate-200 p-6 hover:border-[#1E40AF]/40 hover:shadow-lg transition-all group">
                  <p className="text-[11px] font-data uppercase tracking-widest text-[#1E40AF]">{c.category}</p>
                  <h3 className="mt-2 font-bold text-[#0B192C] group-hover:text-[#1E40AF] transition-colors">{c.name}</h3>
                  <p className="text-xs text-slate-500 mt-1">{c.level} • {c.duration}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {relatedUnis.length > 0 && (
        <section className="py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHead overline="Universities" title={`UNIVERSITIES IN ${country.name.toUpperCase()}`} />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedUnis.map((u) => (
                <Link key={u.id} to={`/universities/${u.slug}`} data-testid={`country-university-${u.slug}`}
                  className="bg-white rounded-xl border border-slate-200 p-6 hover:border-[#1E40AF]/40 hover:shadow-lg transition-all">
                  <h3 className="font-bold text-[#0B192C]">{u.name}</h3>
                  <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{u.city}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {countryFaqs.length > 0 && (
        <section className="py-14 bg-[#F8FAFC] border-t border-slate-200">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <SectionHead overline="FAQ" title={`STUDY IN ${country.name.toUpperCase()} — QUESTIONS`} />
            <div className="space-y-3">
              {countryFaqs.map((f) => (
                <details key={f.id} className="group bg-white border border-slate-200 rounded-xl px-6 py-4 open:border-[#1E40AF]/40 open:shadow-lg transition-all">
                  <summary className="cursor-pointer text-[15px] font-bold text-[#0B192C] list-none flex justify-between items-center">
                    {f.question}<ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform shrink-0 ml-3" />
                  </summary>
                  <p className="text-sm text-slate-600 leading-relaxed pt-3">{f.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-14"><div className="max-w-3xl mx-auto px-4 sm:px-6"><CTABanner title={`Thinking about ${country.name}?`} sub="Book a free counselling session and get a country shortlist matched to your profile." /></div></section>
    </div>
  );
}

/* ---------------- COURSES ---------------- */

export function CoursesPage() {
  useMeta("Explore Courses Abroad — MBA, CS, Engineering, Healthcare & More | Harwell Global Education",
    "Browse international courses by subject, country and degree level: MBA, Data Science, Computer Science, Engineering, Healthcare, Finance and more.");
  const [courses, loaded] = useCollection("courses");
  const [params] = useSearchParams();
  const [cat, setCat] = useState(params.get("category") || "");
  const [level, setLevel] = useState("");
  const [country, setCountry] = useState("");
  const [q, setQ] = useState("");

  const categories = useMemo(() => [...new Set(courses.map((c) => c.category).filter(Boolean))], [courses]);
  const countries = useMemo(() => [...new Set(courses.flatMap((c) => (c.countries || "").split(",").map((x) => x.trim())).filter(Boolean))], [courses]);

  const filtered = courses.filter((c) =>
    (!cat || c.category === cat) &&
    (!level || c.level === level || c.level === "Both") &&
    (!country || (c.countries || "").includes(country)) &&
    (!q || c.name.toLowerCase().includes(q.toLowerCase()))
  );

  const sel = "rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/30";

  return (
    <div data-testid="courses-page">
      <LightHero overline="Course Directory" title="EXPLORE" accent="COURSES."
        sub="Filter international courses by subject, country, degree level and more — then discuss the right fit with a counsellor." />
      <section className="py-14 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={[["Home", "/"], ["Courses"]]} />
          <div className="grid md:grid-cols-4 gap-3 mb-10">
            <div className="relative md:col-span-1">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="course-search" placeholder="Search courses..." value={q} onChange={(e) => setQ(e.target.value)}
                className="w-full rounded-xl border border-slate-300 bg-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/30" />
            </div>
            <select data-testid="course-filter-category" value={cat} onChange={(e) => setCat(e.target.value)} className={sel}>
              <option value="">All Subjects</option>
              {categories.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select data-testid="course-filter-level" value={level} onChange={(e) => setLevel(e.target.value)} className={sel}>
              <option value="">All Levels</option>
              <option>Bachelor's</option>
              <option>Master's</option>
            </select>
            <select data-testid="course-filter-country" value={country} onChange={(e) => setCountry(e.target.value)} className={sel}>
              <option value="">All Countries</option>
              {countries.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((c, i) => (
              <motion.div key={c.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.07 }} whileHover={{ y: -6 }}>
                <Link to={`/courses/${c.slug}`} data-testid={`course-card-${c.slug}`}
                  className="block bg-white rounded-2xl border border-slate-200 p-7 shadow-sm hover:shadow-xl hover:border-[#1E40AF]/40 transition-all h-full group">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[11px] font-data font-semibold uppercase tracking-widest text-[#1E40AF] bg-[#E0F2FE] px-2.5 py-1 rounded-full">{c.level}</span>
                    <span className="text-[11px] font-data text-slate-400">{c.duration}</span>
                  </div>
                  <h3 className="text-lg font-bold text-[#0B192C] group-hover:text-[#1E40AF] transition-colors">{c.name}</h3>
                  <p className="text-sm text-slate-500 mt-1">{c.category}</p>
                  <p className="text-sm text-slate-600 mt-3 leading-relaxed line-clamp-2">{c.overview}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#1E40AF]">
                    VIEW COURSE <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
          {loaded && filtered.length === 0 && <p className="text-center text-slate-400 py-14" data-testid="courses-empty">No courses match your filters.</p>}
          <CTABanner title="Can't decide between courses?" sub="A counsellor can map your academic background to the right course and country options." />
        </div>
      </section>
    </div>
  );
}

export function CoursePage() {
  const { slug } = useParams();
  const [courses, loaded] = useCollection("courses");
  const course = courses.find((c) => c.slug === slug);
  useMeta(
    course ? `${course.name} Abroad — Universities, Requirements & Careers | Harwell Global Education` : "Courses | Harwell Global Education",
    course?.overview || ""
  );
  if (loaded && !course) return <div className="pt-44 pb-32 text-center"><h1 className="text-2xl font-extrabold text-[#0B192C]">Course not found</h1>
    <Link to="/courses" className="mt-4 inline-block text-sm font-bold text-[#1E40AF]">All courses</Link></div>;
  if (!course) return <div className="pt-44 pb-32 text-center text-slate-400">Loading...</div>;

  const related = courses.filter((c) => c.category === course.category && c.id !== course.id).slice(0, 3);
  const sections = [
    ["Who is this course suitable for?", course.who_for],
    ["Typical academic background", course.background],
    ["Key subjects", course.subjects],
    ["Career relevance", course.career],
  ].filter(([, v]) => v);

  return (
    <div data-testid={`course-page-${slug}`}>
      <LightHero overline={course.category} title={course.name.toUpperCase()} sub={course.overview}>
        <div className="mt-6 flex flex-wrap gap-2.5 text-xs font-data text-slate-500">
          <span className="bg-white border border-slate-200 rounded-full px-3.5 py-1.5">LEVEL — {course.level}</span>
          {course.duration && <span className="bg-white border border-slate-200 rounded-full px-3.5 py-1.5">DURATION — {course.duration}</span>}
          {course.mode && <span className="bg-white border border-slate-200 rounded-full px-3.5 py-1.5">MODE — {course.mode}</span>}
        </div>
        <div className="mt-5"><Breadcrumbs items={[["Home", "/"], ["Courses", "/courses"], [course.name]]} /></div>
      </LightHero>
      <section className="py-14 lg:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {sections.map(([h, v]) => (
            <div key={h}>
              <h2 className="text-xl sm:text-2xl font-extrabold text-[#0B192C] tracking-tight mt-10 mb-4">{h}</h2>
              <p className="text-slate-600 leading-relaxed">{v}</p>
            </div>
          ))}
          {course.countries && (
            <>
              <h2 className="text-xl sm:text-2xl font-extrabold text-[#0B192C] tracking-tight mt-10 mb-4">Countries where you can explore this course</h2>
              <div className="flex flex-wrap gap-2.5">
                {course.countries.split(",").map((cn) => cn.trim()).map((cn) => (
                  <span key={cn} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-700">{cn}</span>
                ))}
              </div>
            </>
          )}
          <Disclaimer />
          {related.length > 0 && (
            <>
              <h2 className="text-xl sm:text-2xl font-extrabold text-[#0B192C] tracking-tight mt-12 mb-5">Related courses</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {related.map((c) => (
                  <Link key={c.id} to={`/courses/${c.slug}`} className="bg-white rounded-xl border border-slate-200 p-5 hover:border-[#1E40AF]/40 hover:shadow-lg transition-all">
                    <h3 className="font-bold text-[#0B192C] text-sm">{c.name}</h3>
                    <p className="text-xs text-slate-500 mt-1">{c.level} • {c.duration}</p>
                  </Link>
                ))}
              </div>
            </>
          )}
          <CTABanner title={`Interested in ${course.name}?`} sub="Discuss universities, countries and admission requirements with a counsellor — free." />
        </div>
      </section>
    </div>
  );
}

/* ---------------- UNIVERSITIES ---------------- */

export function UniversitiesPage() {
  useMeta("Explore Universities — Browse by Country, City & Course | Harwell Global Education",
    "Browse universities Harwell Global Education helps students explore, by country, city, course and degree level.");
  const [unis, loaded] = useCollection("universities");
  const [country, setCountry] = useState("");
  const [q, setQ] = useState("");
  const countries = useMemo(() => [...new Set(unis.map((u) => u.country).filter(Boolean))], [unis]);
  const filtered = unis.filter((u) =>
    (!country || u.country === country) &&
    (!q || `${u.name} ${u.city} ${u.fields}`.toLowerCase().includes(q.toLowerCase()))
  );
  const sel = "rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/30";

  return (
    <div data-testid="universities-page">
      <LightHero overline="University Directory" title="EXPLORE" accent="UNIVERSITIES."
        sub="Discover universities matched to your academic profile and budget — browse by country, city, course or degree level." />
      <section className="py-14 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={[["Home", "/"], ["Universities"]]} />
          <div className="flex flex-col md:flex-row gap-3 mb-10">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="university-search" placeholder="Search universities, cities, fields..." value={q} onChange={(e) => setQ(e.target.value)}
                className="w-full rounded-xl border border-slate-300 bg-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/30" />
            </div>
            <select data-testid="university-filter-country" value={country} onChange={(e) => setCountry(e.target.value)} className={sel}>
              <option value="">All Countries</option>
              {countries.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((u, i) => (
              <motion.div key={u.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.07 }} whileHover={{ y: -6 }}>
                <Link to={`/universities/${u.slug}`} data-testid={`university-card-${u.slug}`}
                  className="block bg-white rounded-2xl border border-slate-200 p-7 shadow-sm hover:shadow-xl hover:border-[#1E40AF]/40 transition-all h-full group">
                  <div className="w-12 h-12 rounded-xl bg-[#0B192C] flex items-center justify-center font-black text-white text-sm mb-5">
                    {u.name.split(" ").filter((w) => w[0] === w[0]?.toUpperCase()).slice(0, 2).map((w) => w[0]).join("")}
                  </div>
                  <h3 className="text-lg font-bold text-[#0B192C] group-hover:text-[#1E40AF] transition-colors leading-snug">{u.name}</h3>
                  <p className="text-xs font-data text-slate-500 mt-1.5 flex items-center gap-1"><MapPin className="w-3 h-3" />{u.city}{u.country ? `, ${u.country}` : ""}</p>
                  <p className="text-sm text-slate-600 mt-3 line-clamp-2">{u.fields}</p>
                  {u.badge && <p className="text-xs text-slate-400 mt-3 pt-3 border-t border-slate-100">{u.badge}</p>}
                </Link>
              </motion.div>
            ))}
          </div>
          {loaded && filtered.length === 0 && <p className="text-center text-slate-400 py-14">No universities match your filters.</p>}
          <p className="text-xs text-slate-400 text-center mt-10 max-w-2xl mx-auto">
            Listings reflect universities Harwell helps students explore and do not imply official partnership. Information is subject to each university's latest official updates.
          </p>
          <CTABanner title="Need help shortlisting universities?" sub="Get a personalised university shortlist based on your academics, budget and goals." />
        </div>
      </section>
    </div>
  );
}

export function UniversityPage() {
  const { slug } = useParams();
  const [unis, loaded] = useCollection("universities");
  const u = unis.find((x) => x.slug === slug);
  useMeta(u ? `${u.name} — Courses, Requirements & Fees | Harwell Global Education` : "Universities | Harwell Global Education",
    u ? `${u.name}, ${u.city}. Overview, popular courses, entry requirements, intakes and application information.` : "");
  if (loaded && !u) return <div className="pt-44 pb-32 text-center"><h1 className="text-2xl font-extrabold text-[#0B192C]">University not found</h1>
    <Link to="/universities" className="mt-4 inline-block text-sm font-bold text-[#1E40AF]">All universities</Link></div>;
  if (!u) return <div className="pt-44 pb-32 text-center text-slate-400">Loading...</div>;

  const rows = [
    ["About", u.about], ["Popular courses", u.popular_courses], ["Degree levels", u.degree_levels],
    ["Entry requirements", u.entry_requirements], ["Application information", u.application_info],
    ["Intakes", u.intakes], ["Tuition fee information", u.tuition_info], ["Scholarships", u.scholarships_info],
    ["Accommodation", u.accommodation],
  ].filter(([, v]) => v);

  return (
    <div data-testid={`university-page-${slug}`}>
      <LightHero overline="University Profile" title={u.name.toUpperCase()}
        sub={`${u.city}${u.country ? `, ${u.country}` : ""}${u.badge ? ` — ${u.badge}` : ""}`}>
        <div className="mt-5"><Breadcrumbs items={[["Home", "/"], ["Universities", "/universities"], [u.name]]} /></div>
      </LightHero>
      <section className="py-14 lg:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {rows.length === 0 && (
            <p className="text-slate-600 leading-relaxed">{u.fields}</p>
          )}
          {rows.map(([h, v]) => (
            <div key={h}>
              <h2 className="text-xl font-extrabold text-[#0B192C] tracking-tight mt-10 mb-3">{h}</h2>
              <p className="text-slate-600 leading-relaxed">{v}</p>
            </div>
          ))}
          {u.website && (
            <a href={u.website} target="_blank" rel="noopener noreferrer" data-testid="university-website-link"
              className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-[#1E40AF] hover:text-[#1D4ED8]">
              VISIT OFFICIAL WEBSITE <ExternalLink className="w-4 h-4" />
            </a>
          )}
          <Disclaimer />
          <CTABanner title={`Considering ${u.name}?`} sub="Book a free counselling session to check your eligibility and application timeline." />
        </div>
      </section>
    </div>
  );
}

/* ---------------- FIND YOUR STUDY PATH ---------------- */

const PATH_STEPS = [
  { key: "studied", label: "What did you study?", options: ["Class 12", "Diploma", "Bachelor's degree", "Master's degree", "Other"] },
  { key: "level", label: "What level are you looking for?", options: ["Bachelor's", "Master's", "MBA / Management", "Not sure yet"] },
  { key: "subjects", label: "What subjects interest you?", multi: true, options: ["Business & Management", "Computer Science & IT", "Engineering", "Healthcare & Life Sciences", "Finance & Economics", "Hospitality & Tourism", "Social Sciences"] },
  { key: "countries", label: "Which countries are you considering?", multi: true, options: ["United Kingdom", "Germany", "Italy", "Romania", "France", "Ireland", "Open to suggestions"] },
  { key: "budget", label: "What is your approximate yearly budget?", options: ["Under €4,000", "€4,000 - €8,000", "€8,000 - €15,000", "Above €15,000", "Need guidance"] },
  { key: "start", label: "When do you want to start?", options: ["Within 6 months", "6-12 months", "1-2 years", "Just exploring"] },
  { key: "goals", label: "What are your long-term academic/career interests?", options: ["Corporate / industry careers", "Research & academia", "Entrepreneurship", "Public sector / NGOs", "Still exploring"] },
];

export function FindYourPath() {
  useMeta("Find Your Study Path — Free Discovery Tool | Harwell Global Education",
    "Answer seven quick questions and get a preliminary study path — suggested countries, course categories and degree levels. Free educational guidance.");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const current = PATH_STEPS[step];

  const toggle = (opt) => {
    if (current.multi) {
      const cur = answers[current.key] || [];
      setAnswers({ ...answers, [current.key]: cur.includes(opt) ? cur.filter((o) => o !== opt) : [...cur, opt] });
    } else {
      setAnswers({ ...answers, [current.key]: opt });
    }
  };

  const valid = () => {
    const v = answers[current.key];
    return current.multi ? (v || []).length > 0 : Boolean(v);
  };

  const result = useMemo(() => {
    if (!done) return null;
    const level = answers.level === "Not sure yet"
      ? (answers.studied === "Class 12" || answers.studied === "Diploma" ? "Bachelor's" : "Master's")
      : answers.level;
    let countries = (answers.countries || []).filter((c) => c !== "Open to suggestions");
    if (countries.length === 0) countries = ["United Kingdom", "Germany", "Italy", "Romania", "France", "Ireland"];
    if (answers.budget === "Under €4,000") {
      countries = [...new Set(["Romania", "Italy", "Germany", ...countries])];
    }
    return { level, subjects: answers.subjects || [], countries: countries.slice(0, 4) };
  }, [done, answers]);

  return (
    <div data-testid="find-your-path-page">
      <LightHero overline="Interactive Tool" title="FIND YOUR" accent="STUDY PATH."
        sub="Seven quick questions. Get a preliminary study path — then refine it with a counsellor." />
      <section className="py-16 lg:py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          {!done ? (
            <Reveal className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 sm:p-10">
              <div className="flex items-center gap-2 mb-8">
                {PATH_STEPS.map((s, i) => (
                  <div key={s.key} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-[#1E40AF]" : "bg-slate-200"}`} />
                ))}
              </div>
              <p className="text-xs font-data uppercase tracking-[0.25em] text-[#1E40AF] font-semibold mb-2">Question {step + 1} of {PATH_STEPS.length}</p>
              <h3 className="text-xl font-extrabold text-[#0B192C] mb-6">{current.label}</h3>
              <div className="flex flex-wrap gap-2.5">
                {current.options.map((opt) => {
                  const selected = current.multi ? (answers[current.key] || []).includes(opt) : answers[current.key] === opt;
                  return (
                    <button key={opt} onClick={() => toggle(opt)} data-testid={`path-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`px-5 py-3 rounded-xl text-sm font-semibold border transition-all ${
                        selected ? "bg-[#1E40AF] text-white border-[#1E40AF]" : "bg-white text-slate-700 border-slate-300 hover:border-[#1E40AF] hover:text-[#1E40AF]"
                      }`}>
                      {opt}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-9">
                <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} data-testid="path-back-btn"
                  className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 disabled:opacity-0">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                  onClick={() => {
                    if (!valid()) return;
                    if (step < PATH_STEPS.length - 1) setStep(step + 1);
                    else { track("study_path_complete"); setDone(true); }
                  }}
                  data-testid="path-next-btn"
                  className="px-7 py-3 rounded-full text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] shadow-lg shadow-blue-900/20 active:scale-[0.98] transition-all flex items-center gap-2">
                  {step === PATH_STEPS.length - 1 ? "SEE MY STUDY PATH" : "CONTINUE"} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </Reveal>
          ) : (
            <Reveal className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 sm:p-10" data-testid="path-result">
              <p className="text-xs font-data uppercase tracking-[0.25em] text-[#1E40AF] font-semibold mb-2">Your Initial Study Path</p>
              <h3 className="text-2xl font-extrabold text-[#0B192C]">Here's a starting point</h3>
              <div className="mt-7 space-y-6">
                <div>
                  <p className="text-xs font-data uppercase tracking-widest text-slate-400 mb-2.5">Suggested Degree Level</p>
                  <span className="inline-block px-4 py-2 rounded-full bg-[#E0F2FE] text-[#1E40AF] text-sm font-bold">{result.level}</span>
                </div>
                <div>
                  <p className="text-xs font-data uppercase tracking-widest text-slate-400 mb-2.5">Course Categories</p>
                  <div className="flex flex-wrap gap-2">{result.subjects.map((s) => (
                    <span key={s} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-700">{s}</span>
                  ))}</div>
                </div>
                <div>
                  <p className="text-xs font-data uppercase tracking-widest text-slate-400 mb-2.5">Countries To Explore</p>
                  <div className="flex flex-wrap gap-2">{result.countries.map((c) => (
                    <span key={c} className="px-4 py-2 rounded-full bg-[#FEF3C7] border border-[#FDE68A] text-sm font-semibold text-[#92400E]">{c}</span>
                  ))}</div>
                </div>
              </div>
              <p className="mt-8 text-xs text-slate-400 leading-relaxed border-t border-slate-100 pt-5">
                This is preliminary educational guidance based on your answers — not a guaranteed admission recommendation.
                Final options depend on your complete academic profile and each university's current requirements.
              </p>
              <button onClick={() => navigate("/contact")} data-testid="path-counsellor-btn"
                className="mt-7 w-full py-4 rounded-xl text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] active:scale-[0.98] transition-all">
                DISCUSS YOUR OPTIONS WITH A COUNSELLOR
              </button>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}

/* ---------------- SERVICES ---------------- */

export function ServicesPage() {
  useMeta("How Harwell Global Education Supports You — 7-Step Journey | Harwell Global",
    "From profile assessment to pre-departure guidance — see how Harwell Global Education supports your study-abroad journey step by step.");
  const steps = [
    { icon: Compass, title: "Profile Assessment", desc: "We understand your academic background, interests, goals and constraints." },
    { icon: BookOpen, title: "Course & Career Discussion", desc: "We discuss course directions aligned with your long-term plans." },
    { icon: Globe2, title: "Country Selection", desc: "Compare destinations on academics, costs, lifestyle and requirements." },
    { icon: ListChecks, title: "University & Course Shortlisting", desc: "A shortlist built around your profile — not commissions." },
    { icon: FileCheck, title: "Application & Documentation Support", desc: "We help you prepare and organise a complete application file." },
    { icon: Stamp, title: "Visa & Documentation Guidance", desc: "Structured guidance for the student visa process and documentation." },
    { icon: Luggage, title: "Pre-Departure Guidance", desc: "Prepare for travel, accommodation and your first weeks abroad." },
  ];
  return (
    <div data-testid="services-page">
      <LightHero overline="Our Services" title="HOW HARWELL GLOBAL EDUCATION" accent="SUPPORTS YOU."
        sub="A structured, transparent journey — from your first counselling session to your arrival at university." />
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {steps.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.04} className="flex gap-6 pb-10 last:pb-0 relative">
              {i < steps.length - 1 && <div className="absolute left-7 top-16 bottom-0 w-px bg-slate-200" />}
              <div className="relative shrink-0 w-14 h-14 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center">
                <s.icon className="w-6 h-6 text-[#1E40AF]" />
              </div>
              <div className="pt-1">
                <p className="font-data text-xs text-[#B45309] font-semibold tracking-widest">STEP {i + 1}</p>
                <h3 className="mt-1.5 text-lg font-extrabold text-[#0B192C]">{s.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
          <div className="mt-12 flex gap-3 bg-[#E0F2FE] border border-sky-200 rounded-xl p-5">
            <ShieldCheck className="w-5 h-5 text-[#1E40AF] shrink-0 mt-0.5" />
            <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
              We help students understand their options and navigate the application journey. Final admission and visa
              decisions are made by the relevant university or government authority.
            </p>
          </div>
          <CTABanner title="Ready for Step 1?" sub="Book a free profile assessment with a Harwell counsellor." />
        </div>
      </section>
    </div>
  );
}

/* ---------------- SCHOLARSHIPS ---------------- */

export function ScholarshipsPage() {
  useMeta("Scholarships & Funding for Studying Abroad | Harwell Global Education",
    "Explore university, merit-based, country-level and government scholarships, plus funding and education loan guidance.");
  const [items, loaded] = useCollection("scholarships");
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const countries = useMemo(() => [...new Set(items.map((s) => s.country).filter(Boolean))], [items]);
  const filtered = items.filter((s) =>
    (!country || s.country === country) &&
    (!q || `${s.name} ${s.institution} ${s.eligibility}`.toLowerCase().includes(q.toLowerCase()))
  );

  const types = [
    ["University Scholarships", "Offered directly by universities to international applicants, often based on academic merit."],
    ["Merit-Based Scholarships", "Awarded for strong academic records, test scores or achievements."],
    ["Country-Level Scholarships", "National schemes that support international students in specific destinations."],
    ["Government Scholarships", "Funded by governments to attract international talent."],
    ["Funding & Financial Planning", "Structured budgeting for tuition and living costs across your study period."],
    ["Education Loan Guidance", "Understanding loan options, documentation and repayment structures."],
  ];

  return (
    <div data-testid="scholarships-page">
      <LightHero overline="Funding" title="SCHOLARSHIPS &" accent="FUNDING."
        sub="Understand the scholarship landscape — and plan your funding with clear, verified information." />
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
            {types.map(([t, d], i) => (
              <Reveal key={t} delay={(i % 3) * 0.07} className="bg-white rounded-2xl border border-slate-200 p-7 shadow-sm">
                <BadgeCheck className="w-6 h-6 text-[#B45309] mb-4" />
                <h3 className="font-bold text-[#0B192C]">{t}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{d}</p>
              </Reveal>
            ))}
          </div>

          <SectionHead overline="Listings" title="SCHOLARSHIP LISTINGS" />
          <div className="flex flex-col md:flex-row gap-3 mb-8 max-w-3xl mx-auto">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="scholarship-search" placeholder="Search scholarships..." value={q} onChange={(e) => setQ(e.target.value)}
                className="w-full rounded-xl border border-slate-300 bg-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/30" />
            </div>
            <select data-testid="scholarship-filter-country" value={country} onChange={(e) => setCountry(e.target.value)}
              className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/30">
              <option value="">All Countries</option>
              {countries.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>

          {filtered.length > 0 ? (
            <div className="space-y-4 max-w-4xl mx-auto">
              {filtered.map((s) => (
                <div key={s.id} data-testid={`scholarship-card-${s.id}`} className="bg-white rounded-xl border border-slate-200 p-6 hover:border-[#1E40AF]/40 hover:shadow-lg transition-all">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="font-bold text-[#0B192C]">{s.name}</h3>
                      <p className="text-xs text-slate-500 mt-1">{s.institution}{s.country ? ` • ${s.country}` : ""}</p>
                    </div>
                    {s.last_verified && <span className="text-[11px] font-data text-slate-400">Verified: {s.last_verified}</span>}
                  </div>
                  {s.eligibility && <p className="text-sm text-slate-600 mt-3"><strong className="text-slate-700">Eligibility:</strong> {s.eligibility}</p>}
                  {s.funding && <p className="text-sm text-slate-600 mt-1.5"><strong className="text-slate-700">Funding:</strong> {s.funding}</p>}
                  <div className="flex flex-wrap items-center gap-4 mt-4 text-xs">
                    {s.deadline && <span className="font-data text-[#B45309]">Deadline: {s.deadline}</span>}
                    {s.source_url && (
                      <a href={s.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-bold text-[#1E40AF]">
                        Official source <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="max-w-2xl mx-auto rounded-2xl border-2 border-dashed border-slate-300 bg-white p-10 text-center" data-testid="scholarships-empty">
              <BadgeCheck className="w-9 h-9 text-slate-300 mx-auto mb-3" />
              <h3 className="font-bold text-[#0B192C]">Verified listings being added</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                We only publish scholarship details we can verify with official providers, including current deadlines.
                Ask a counsellor for the latest options for your profile.
              </p>
            </div>
          )}
          <p className="text-xs text-slate-400 text-center mt-8 max-w-xl mx-auto">
            Always verify scholarship availability, eligibility and deadlines with the official provider.
          </p>
          <CTABanner title="Want scholarship options for your profile?" sub="A counsellor can identify scholarships worth pursuing for your course and country." />
        </div>
      </section>
    </div>
  );
}

/* ---------------- VISA HUB ---------------- */

const VISA_COUNTRIES = ["United Kingdom", "Germany", "Italy", "Romania", "France", "Ireland"];
const VISA_DOCS = [
  ["Passport", "Valid for the full duration of your intended studies, with sufficient blank pages."],
  ["Academic documents", "Transcripts, certificates and authenticated/apostilled copies where required."],
  ["Financial documents", "Evidence of funds for tuition and living costs, in the format the visa authority expects."],
  ["Language requirements", "IELTS/TOEFL or medium-of-instruction evidence, depending on the university."],
  ["Admission documents", "Your offer/acceptance letter and enrolment confirmations."],
  ["Insurance", "Health or travel insurance where applicable for the destination."],
];
const VISA_MISTAKES = [
  "Incomplete or inconsistent financial documentation",
  "Name or date mismatches across documents",
  "Applying too close to the intake deadline",
  "Unauthenticated copies where attestation is required",
  "Not preparing for credibility or interview questions",
];

export function VisaHubPage() {
  useMeta("Visa & Documentation Guidance — UK, Germany, Italy, Romania, France, Ireland | Harwell Global",
    "Educational guidance on student visa documentation for the UK, Germany, Italy, Romania, France and Ireland — checklists, process overview and common mistakes.");
  const [active, setActive] = useState("Romania");
  return (
    <div data-testid="visa-hub-page">
      <LightHero overline="Visa & Documentation Hub" title="VISA & DOCUMENTATION" accent="GUIDANCE."
        sub="Country-wise educational guidance on documents, process and preparation — so your file is complete and consistent." />
      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap gap-2.5 mb-10" data-testid="visa-country-tabs">
            {VISA_COUNTRIES.map((c) => (
              <button key={c} onClick={() => setActive(c)} data-testid={`visa-tab-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all ${active === c ? "bg-[#0B192C] text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-[#1E40AF]"}`}>
                {c}
              </button>
            ))}
          </div>

          <Reveal className="bg-white rounded-2xl border border-slate-200 p-7 sm:p-10 shadow-sm">
            <h2 className="text-xl sm:text-2xl font-extrabold text-[#0B192C] tracking-tight">{active} — student visa overview</h2>
            <p className="text-slate-600 leading-relaxed mt-3">
              The {active} student visa process typically begins after you receive your university acceptance. You then
              prepare a documentation file — identity, academics, finances, language evidence and insurance — and submit
              it to the relevant visa authority. Requirements and processing times vary and can change; we guide you using
              the latest official checklists.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {VISA_DOCS.map(([t, d]) => (
                <div key={t} className="flex gap-3 border border-slate-100 rounded-xl p-4">
                  <Check className="w-4 h-4 text-[#059669] shrink-0 mt-0.5" />
                  <div><p className="text-sm font-bold text-[#0B192C]">{t}</p><p className="text-xs text-slate-500 mt-1 leading-relaxed">{d}</p></div>
                </div>
              ))}
            </div>
            <h3 className="text-lg font-extrabold text-[#0B192C] mt-10 mb-4">Common documentation mistakes</h3>
            <ul className="space-y-2.5">
              {VISA_MISTAKES.map((m) => (
                <li key={m} className="flex gap-3 text-sm text-slate-600"><Info className="w-4 h-4 text-[#B45309] shrink-0 mt-0.5" />{m}</li>
              ))}
            </ul>
          </Reveal>
          <Disclaimer />
          <CTABanner title={`Applying for a ${active} student visa?`} sub="Get structured visa documentation guidance from Harwell Global Education." />
        </div>
      </section>
    </div>
  );
}

/* ---------------- CHECKLIST ---------------- */

const CHECKLIST = [
  "Choose the right country", "Choose the right course", "Shortlist universities", "Check admission requirements",
  "Calculate tuition & living costs", "Explore scholarships", "Prepare documents", "Submit applications",
  "Review visa requirements", "Prepare for departure",
];

export function ChecklistPage() {
  useMeta("Your Study Abroad Checklist | Harwell Global Education",
    "An interactive 10-step study abroad checklist — from choosing a country to preparing for departure.");
  const [done, setDone] = useState([]);
  const toggle = (i) => setDone(done.includes(i) ? done.filter((x) => x !== i) : [...done, i]);
  const pct = Math.round((done.length / CHECKLIST.length) * 100);
  const navigate = useNavigate();

  return (
    <div data-testid="checklist-page">
      <LightHero overline="Planning Tool" title="YOUR STUDY ABROAD" accent="CHECKLIST."
        sub="Ten steps between you and departure. Tick them off as you go." />
      <section className="py-16 lg:py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <Reveal className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 sm:p-10">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-data uppercase tracking-widest text-slate-400">Progress</p>
              <p className="text-sm font-data font-bold text-[#1E40AF]" data-testid="checklist-progress">{pct}%</p>
            </div>
            <div className="h-2 bg-slate-100 rounded-full mb-8 overflow-hidden">
              <div className="h-full bg-[#1E40AF] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <div className="space-y-3">
              {CHECKLIST.map((item, i) => (
                <button key={item} onClick={() => toggle(i)} data-testid={`checklist-item-${i}`}
                  className={`w-full flex items-center gap-4 rounded-xl border p-4 text-left transition-all ${
                    done.includes(i) ? "border-[#059669]/40 bg-emerald-50/50" : "border-slate-200 bg-white hover:border-[#1E40AF]/40"
                  }`}>
                  <span className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-all ${
                    done.includes(i) ? "bg-[#059669] border-[#059669]" : "border-slate-300"
                  }`}>
                    {done.includes(i) && <Check className="w-4 h-4 text-white" />}
                  </span>
                  <span className={`text-sm font-semibold ${done.includes(i) ? "text-slate-400 line-through" : "text-[#0B192C]"}`}>{item}</span>
                </button>
              ))}
            </div>
          </Reveal>
          <div className="text-center mt-10">
            <p className="font-extrabold text-[#0B192C] text-lg">NOT SURE WHERE TO START?</p>
            <button onClick={() => navigate("/contact")} data-testid="checklist-cta"
              className="mt-4 px-8 py-4 rounded-full text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] active:scale-[0.98] transition-all">
              TALK TO HARWELL GLOBAL EDUCATION
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------------- UPDATES ---------------- */

export function UpdatesPage() {
  useMeta("Global Education Updates — Policy, Intakes & Announcements | Harwell Global Education",
    "Verified international education updates: policy changes, university announcements, intake updates and scholarship deadlines.");
  const [items, loaded] = useCollection("updates");
  return (
    <div data-testid="updates-page">
      <LightHero overline="Newsroom" title="GLOBAL EDUCATION" accent="UPDATES."
        sub="Verified policy updates, university announcements, intake news and scholarship deadlines — clearly dated and sourced." />
      <section className="py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {items.length > 0 ? (
            <div className="space-y-5">
              {items.map((u) => (
                <Reveal key={u.id} className="bg-white rounded-2xl border border-slate-200 p-7 shadow-sm" data-testid={`update-card-${u.id}`}>
                  <span className="text-[11px] font-data uppercase tracking-widest text-[#1E40AF] bg-[#E0F2FE] px-2.5 py-1 rounded-full">{u.category}</span>
                  <h3 className="mt-3 text-lg font-bold text-[#0B192C]">{u.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{u.summary}</p>
                  <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-4 text-xs text-slate-400">
                    {u.published_at && <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />Updated: {new Date(u.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>}
                    {u.source && <span>Source: {u.source}</span>}
                  </div>
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white p-10 text-center" data-testid="updates-empty">
              <CalendarDays className="w-9 h-9 text-slate-300 mx-auto mb-3" />
              <h3 className="font-bold text-[#0B192C]">Updates coming soon</h3>
              <p className="text-sm text-slate-500 mt-2">We publish only verified, dated education updates from official sources.</p>
            </div>
          )}
          <CTABanner />
        </div>
      </section>
    </div>
  );
}

/* ---------------- EVENTS ---------------- */

export function EventsPage() {
  useMeta("Upcoming Events — Webinars, Workshops & Counselling Sessions | Harwell Global Education",
    "Join Harwell Global Education events: university information sessions, country webinars, application workshops and counselling events.");
  const [items, loaded] = useCollection("events");
  const navigate = useNavigate();
  return (
    <div data-testid="events-page">
      <LightHero overline="Events" title="UPCOMING" accent="EVENTS."
        sub="University information sessions, country webinars, application workshops and counselling events." />
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {items.length > 0 ? (
            <div className="space-y-5">
              {items.map((e) => (
                <Reveal key={e.id} className="bg-white rounded-2xl border border-slate-200 p-7 shadow-sm flex flex-col sm:flex-row sm:items-center gap-6" data-testid={`event-card-${e.id}`}>
                  <div className="shrink-0 w-20 h-20 rounded-xl bg-[#0B192C] text-white flex flex-col items-center justify-center">
                    <CalendarDays className="w-5 h-5 text-[#38BDF8]" />
                    <span className="text-[10px] font-data mt-1.5 px-1 text-center leading-tight">{e.date}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-[#0B192C]">{e.title}</h3>
                    <p className="text-sm text-slate-500 mt-1">{[e.time, e.location, e.speaker].filter(Boolean).join(" • ")}</p>
                    {e.description && <p className="text-sm text-slate-600 mt-2 leading-relaxed">{e.description}</p>}
                  </div>
                  <button onClick={() => navigate("/contact")} data-testid={`event-register-${e.id}`}
                    className="shrink-0 px-6 py-3 rounded-full text-xs font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] active:scale-[0.98] transition-all">
                    REGISTER
                  </button>
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white p-10 text-center" data-testid="events-empty">
              <CalendarDays className="w-9 h-9 text-slate-300 mx-auto mb-3" />
              <h3 className="font-bold text-[#0B192C]">New events being scheduled</h3>
              <p className="text-sm text-slate-500 mt-2">Webinars and counselling sessions are announced here. Meanwhile, book a free 1-on-1 session anytime.</p>
            </div>
          )}
          <CTABanner title="Can't wait for an event?" sub="Book a free 1-on-1 counselling session at a time that suits you." />
        </div>
      </section>
    </div>
  );
}

/* ---------------- TEST PREPARATION ---------------- */

const TESTS = [
  { name: "IELTS", full: "International English Language Testing System", who: "Most popular English test for UK, Australia, Canada, Ireland, New Zealand and many European universities.", format: "Listening, Reading, Writing and Speaking — Academic or General Training modules.", use: "University admissions and many visa processes.", source: "https://ielts.org" },
  { name: "PTE Academic", full: "Pearson Test of English", who: "Accepted widely in Australia, New Zealand, UK, Canada and beyond.", format: "Fully computer-based: speaking, writing, reading and listening.", use: "University admissions; accepted for several visa categories.", source: "https://www.pearsonpte.com" },
  { name: "TOEFL iBT", full: "Test of English as a Foreign Language", who: "Preferred by many US universities; accepted globally.", format: "Internet-based: reading, listening, speaking, writing.", use: "University admissions, especially in North America.", source: "https://www.ets.org/toefl" },
  { name: "GRE", full: "Graduate Record Examination", who: "Applicants to many US and international graduate programs.", format: "Analytical writing, verbal reasoning, quantitative reasoning.", use: "Master's and PhD admissions; some business schools.", source: "https://www.ets.org/gre" },
  { name: "GMAT", full: "Graduate Management Admission Test", who: "MBA and business Master's applicants.", format: "Quantitative, verbal and data insights sections.", use: "Business school admissions worldwide.", source: "https://www.mba.com" },
  { name: "SAT", full: "Scholastic Assessment Test", who: "Undergraduate applicants, primarily to US universities.", format: "Reading, writing and math; digital format.", use: "Bachelor's admissions in the USA and select institutions elsewhere.", source: "https://www.collegeboard.org" },
  { name: "ACT", full: "American College Testing", who: "Alternative to the SAT for US undergraduate admissions.", format: "English, math, reading, science; optional writing.", use: "US Bachelor's admissions.", source: "https://www.act.org" },
  { name: "Duolingo English Test", full: "Duolingo English Test (DET)", who: "A flexible online English test accepted by a growing list of universities.", format: "Adaptive online test, completed from home.", use: "University admissions where accepted — always confirm acceptance with your target university.", source: "https://englishtest.duolingo.com" },
];

export function TestPrepPage() {
  useMeta("Test Preparation — IELTS, PTE, TOEFL, GRE, GMAT, SAT | Harwell Global Education",
    "Understand IELTS, PTE, TOEFL, GRE, GMAT, SAT, ACT and the Duolingo English Test — who needs them, formats and official preparation resources.");
  return (
    <div data-testid="test-prep-page">
      <LightHero overline="Test Preparation" title="PREPARE FOR YOUR" accent="INTERNATIONAL TEST."
        sub="Understand which test you need, what it covers and how to prepare — with links to official resources." />
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TESTS.map((t, i) => (
              <Reveal key={t.name} delay={(i % 4) * 0.06} className="bg-white rounded-2xl border border-[#E6EBF1] p-6 shadow-sm hover:shadow-lg hover:border-[#1E40AF]/30 transition-all"
                data-testid={`test-card-${t.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="font-data text-xs text-[#B45309] font-semibold tracking-widest">{t.name}</p>
                <h3 className="mt-1.5 font-bold text-[#172033] text-[15px] leading-snug">{t.full}</h3>
                <div className="mt-4 space-y-2.5 text-xs text-[#5F6B7A] leading-relaxed">
                  <p><strong className="text-[#172033]">Who needs it:</strong> {t.who}</p>
                  <p><strong className="text-[#172033]">Format:</strong> {t.format}</p>
                  <p><strong className="text-[#172033]">Typical use:</strong> {t.use}</p>
                </div>
                <a href={t.source} target="_blank" rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-[#1E40AF] hover:text-[#1D4ED8]">
                  OFFICIAL RESOURCES <ExternalLink className="w-3 h-3" />
                </a>
              </Reveal>
            ))}
          </div>
          <p className="text-xs text-slate-400 text-center mt-8 max-w-xl mx-auto">
            Harwell Global Education provides guidance on test selection and preparation planning. We are not an official test provider.
          </p>
          <CTABanner title="Not sure which test you need?" sub="A counsellor will tell you exactly which tests your shortlisted universities accept." />
        </div>
      </section>
    </div>
  );
}

/* ---------------- COST CALCULATOR ---------------- */

const COST_DATA = {
  "Romania": { t: [2000, 4000], l: [5000, 7000] }, "Germany": { t: [0, 1000], l: [11000, 13000] },
  "Italy": { t: [1000, 4000], l: [8000, 11000] }, "France": { t: [3000, 12000], l: [9000, 13000] },
  "United Kingdom": { t: [12000, 25000], l: [12000, 16000] }, "Ireland": { t: [10000, 20000], l: [11000, 14000] },
  "USA": { t: [15000, 40000], l: [12000, 18000] }, "Canada": { t: [15000, 30000], l: [10000, 14000] },
  "Australia": { t: [18000, 32000], l: [14000, 18000] }, "New Zealand": { t: [16000, 26000], l: [12000, 15000] },
  "Netherlands": { t: [8000, 15000], l: [11000, 14000] }, "Switzerland": { t: [1000, 3000], l: [18000, 24000] },
  "Spain": { t: [1500, 6000], l: [8000, 11000] }, "UAE": { t: [10000, 22000], l: [9000, 14000] },
  "Singapore": { t: [15000, 30000], l: [10000, 14000] }, "Malaysia": { t: [4000, 10000], l: [5000, 8000] },
  "Mauritius": { t: [3000, 8000], l: [5000, 7000] },
};

const fmtEUR = (n) => `€${Math.round(n / 100) * 100 > 999 ? (Math.round(n / 100) * 100).toLocaleString("en-US") : Math.round(n / 100) * 100}`;

export function CostCalculatorPage() {
  useMeta("Study Abroad Cost Calculator | Harwell Global Education",
    "Estimate tuition, living costs, first-year budget and total program cost for 17 study destinations. Indicative planning tool.");
  const [country, setCountry] = useState("Romania");
  const [years, setYears] = useState(3);
  const [lifestyle, setLifestyle] = useState(1);

  const d = COST_DATA[country];
  const tLow = d.t[0], tHigh = d.t[1];
  const lLow = Math.round(d.l[0] * lifestyle), lHigh = Math.round(d.l[1] * lifestyle);
  const firstYear = [tLow + lLow, tHigh + lHigh];
  const total = [firstYear[0] * years, firstYear[1] * years];

  const sel = "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/30";

  return (
    <div data-testid="cost-calculator-page">
      <LightHero overline="Planning Tool" title="STUDY ABROAD" accent="COST CALCULATOR."
        sub="Estimate your budget across tuition and living costs — before you shortlist." />
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Reveal className="bg-white rounded-2xl border border-[#E6EBF1] shadow-sm p-7 sm:p-10">
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Country</label>
                <select data-testid="calc-country" value={country} onChange={(e) => setCountry(e.target.value)} className={sel}>
                  {Object.keys(COST_DATA).map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Program duration</label>
                <select data-testid="calc-duration" value={years} onChange={(e) => setYears(Number(e.target.value))} className={sel}>
                  {[1, 1.5, 2, 3, 4, 6].map((y) => <option key={y} value={y}>{y} {y === 1 ? "year" : "years"}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Lifestyle</label>
                <select data-testid="calc-lifestyle" value={lifestyle} onChange={(e) => setLifestyle(Number(e.target.value))} className={sel}>
                  <option value={0.9}>Careful budget</option>
                  <option value={1}>Balanced</option>
                  <option value={1.15}>Comfortable</option>
                </select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-8" data-testid="calc-results">
              {[
                ["Estimated tuition / year", `${fmtEUR(tLow)} - ${fmtEUR(tHigh)}`],
                ["Estimated living cost / year", `${fmtEUR(lLow)} - ${fmtEUR(lHigh)}`],
                ["Estimated first-year budget", `${fmtEUR(firstYear[0])} - ${fmtEUR(firstYear[1])}`],
                ["Estimated total program cost", `${fmtEUR(total[0])} - ${fmtEUR(total[1])}`],
              ].map(([label, val], i) => (
                <div key={label} className={`rounded-xl p-5 ${i >= 2 ? "bg-[#EEF5FF] border border-[#1E40AF]/20" : "bg-[#F7F9FC] border border-[#E6EBF1]"}`}>
                  <p className="text-[11px] font-data uppercase tracking-widest text-slate-500">{label}</p>
                  <p className="mt-2 text-xl font-black text-[#172033]">{val}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-6 leading-relaxed">
              Estimates are indicative and may vary by university, city, lifestyle and exchange rates. Figures are broad
              annual ranges for planning only — always verify current tuition and living costs with official university sources.
            </p>
          </Reveal>
          <CTABanner title="Want an exact budget for your shortlist?" sub="A counsellor builds a personalised cost plan for your chosen universities." />
        </div>
      </section>
    </div>
  );
}

/* ---------------- INTAKE PLANNER ---------------- */

const INTAKES = [
  { c: "United Kingdom", intakes: [["September/October", "Apply 9-12 months ahead"], ["January", "Apply 6-9 months ahead"]] },
  { c: "USA", intakes: [["Fall (Aug/Sep)", "Apply 10-12 months ahead"], ["Spring (Jan)", "Apply 8-10 months ahead"]] },
  { c: "Canada", intakes: [["Fall (Sep)", "Apply 9-12 months ahead"], ["Winter (Jan)", "Apply 8-10 months ahead"], ["Summer (May)", "Limited programs"]] },
  { c: "Australia", intakes: [["February", "Apply 8-10 months ahead"], ["July", "Apply 6-8 months ahead"]] },
  { c: "Germany", intakes: [["Winter (Oct)", "Apply 8-10 months ahead"], ["Summer (Apr)", "Limited programs"]] },
  { c: "Ireland", intakes: [["September", "Apply 8-10 months ahead"], ["January", "Limited programs"]] },
  { c: "New Zealand", intakes: [["February", "Apply 8-10 months ahead"], ["July", "Apply 6-8 months ahead"]] },
  { c: "France", intakes: [["September", "Apply 8-10 months ahead"], ["January", "Selected institutions"]] },
  { c: "Italy", intakes: [["September/October", "Pre-enrolment months earlier"]] },
  { c: "Romania", intakes: [["Autumn (Sep/Oct)", "Start 6-9 months ahead"]] },
  { c: "Netherlands", intakes: [["September", "Apply 8-10 months ahead"], ["February", "Limited programs"]] },
  { c: "Switzerland", intakes: [["September", "Apply 8-10 months ahead"]] },
  { c: "Spain", intakes: [["September/October", "Apply 8-10 months ahead"]] },
  { c: "UAE", intakes: [["September", "Rolling at many institutions"], ["January", "Common second intake"]] },
  { c: "Singapore", intakes: [["August", "Apply 8-10 months ahead"]] },
  { c: "Malaysia", intakes: [["Multiple intakes yearly", "Institution-specific"]] },
  { c: "Mauritius", intakes: [["January & September", "Institution-specific"]] },
];

export function IntakePlannerPage() {
  useMeta("Find Your Intake — Application Timelines by Country | Harwell Global Education",
    "Compare intake seasons and recommended application windows across 17 study destinations.");
  const [country, setCountry] = useState("");
  const list = INTAKES.filter((i) => !country || i.c === country);
  return (
    <div data-testid="intake-planner-page">
      <LightHero overline="Intake Planner" title="FIND YOUR" accent="INTAKE."
        sub="Compare intake seasons and plan backwards — applications, documents and visa preparation all take time." />
      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <select data-testid="intake-filter" value={country} onChange={(e) => setCountry(e.target.value)}
            className="mb-8 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/30">
            <option value="">All Countries</option>
            {INTAKES.map((i) => <option key={i.c}>{i.c}</option>)}
          </select>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {list.map((i, idx) => (
              <Reveal key={i.c} delay={(idx % 3) * 0.06} className="bg-white rounded-2xl border border-[#E6EBF1] p-6 shadow-sm" data-testid={`intake-card-${i.c.toLowerCase().replace(/\s+/g, "-")}`}>
                <h3 className="font-extrabold text-[#172033]">{i.c}</h3>
                <div className="mt-4 space-y-3">
                  {i.intakes.map(([name, note]) => (
                    <div key={name} className="flex gap-3 border border-slate-100 rounded-xl p-3.5">
                      <CalendarDays className="w-4 h-4 text-[#1E40AF] shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-bold text-[#172033]">{name}</p>
                        <p className="text-xs text-[#5F6B7A] mt-0.5">{note}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 mt-4">Visa preparation typically adds 2-4 months after admission. Verify current deadlines with universities.</p>
              </Reveal>
            ))}
          </div>
          <CTABanner title="Want a personal application timeline?" sub="Tell us your target intake — we'll map every milestone backwards from it." />
        </div>
      </section>
    </div>
  );
}

/* ---------------- FUNDING / EDUCATION LOANS ---------------- */

export function FundingPage() {
  useMeta("Fund Your Education — Loans, Planning & Scholarships | Harwell Global Education",
    "Educational guidance on education loans, financial planning, scholarships and university payment planning for studying abroad.");
  const sections = [
    ["Education Loans", "Understand how education loans for international study typically work — loan amounts, collateral and non-collateral options, interest structures and repayment terms. We explain the landscape; lenders make all approval decisions.", "Lender comparisons, eligibility criteria and documentation checklists."],
    ["Financial Planning", "Map your complete study budget — tuition, living costs, insurance, travel and buffers — before you commit to a university.", "A realistic year-by-year funding plan built around your intake."],
    ["Scholarships", "Scholarships can reduce costs meaningfully, but availability and eligibility change every cycle.", "A verified shortlist of scholarships worth pursuing for your profile."],
    ["University Payment Planning", "Understand tuition deposit schedules, instalment options and payment deadlines at your chosen universities.", "Clear payment calendars so your family never misses a deadline."],
  ];
  return (
    <div data-testid="funding-page">
      <LightHero overline="Funding" title="FUND YOUR" accent="EDUCATION."
        sub="Loans, scholarships and payment planning — explained clearly, without the sales pitch." />
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-6">
          {sections.map(([t, d, w], i) => (
            <Reveal key={t} delay={i * 0.06} className="bg-white rounded-2xl border border-[#E6EBF1] p-8 shadow-sm">
              <p className="font-data text-xs text-[#B45309] font-semibold tracking-widest">0{i + 1}</p>
              <h3 className="mt-2 text-xl font-extrabold text-[#172033]">{t}</h3>
              <p className="mt-2.5 text-sm text-[#5F6B7A] leading-relaxed">{d}</p>
              <p className="mt-3 text-sm text-[#172033]"><strong>How we help:</strong> {w}</p>
            </Reveal>
          ))}
          <p className="text-xs text-slate-400 text-center max-w-xl mx-auto">
            We provide educational guidance only. We do not recommend financial products as guaranteed solutions; lending decisions rest with financial institutions.
          </p>
          <CTABanner title="Plan your funding with a counsellor" sub="Get a realistic funding plan for your shortlisted universities — free." />
        </div>
      </section>
    </div>
  );
}

/* ---------------- STUDENT STORIES PAGE ---------------- */

export function StudentStoriesPage() {
  useMeta("Student Journeys — Real Study Abroad Stories | Harwell Global Education",
    "Real student journeys from Harwell Global Education — profiles, destinations, courses and outcomes, published with student consent.");
  const [stories, loaded] = useCollection("testimonials");
  return (
    <div data-testid="student-stories-page">
      <LightHero overline="Student Journeys" title="DREAMS THAT MOVE" accent="FORWARD."
        sub="Real students, real journeys — published only with consent." />
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loaded && stories.length === 0 ? (
            <div className="max-w-2xl mx-auto rounded-2xl border-2 border-dashed border-slate-300 bg-white p-12 text-center" data-testid="stories-empty">
              <GraduationCap className="w-10 h-10 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#172033]">Student journeys coming soon</h3>
              <p className="mt-3 text-sm text-[#5F6B7A] leading-relaxed">
                We publish genuine student stories only — never fabricated testimonials. As our students begin their
                journeys abroad, their stories will appear here with their consent.
              </p>
              <a href="mailto:Contact@harwellglobal.com?subject=Share%20My%20Journey" data-testid="share-journey-btn"
                className="mt-6 inline-block px-7 py-3.5 rounded-full text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] transition-all">
                SHARE YOUR JOURNEY
              </a>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {stories.map((s, i) => (
                <Reveal key={s.id} delay={(i % 3) * 0.08} className="bg-white rounded-2xl border border-[#E6EBF1] p-8 shadow-sm">
                  <p className="text-sm text-[#5F6B7A] leading-relaxed italic">"{s.quote}"</p>
                  <div className="mt-6 pt-5 border-t border-slate-100">
                    <p className="font-bold text-[#172033] text-sm">{s.name}</p>
                    <p className="text-xs text-slate-500 mt-1">{s.program} — {s.university}, {s.country}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
          <CTABanner />
        </div>
      </section>
    </div>
  );
}

/* ---------------- STUDY ABROAD HUB ---------------- */

const REGIONS = [
  ["Europe", ["United Kingdom", "Germany", "Italy", "Romania", "France", "Ireland", "Netherlands", "Switzerland", "Spain"]],
  ["North America", ["USA", "Canada"]],
  ["Oceania", ["Australia", "New Zealand"]],
  ["Asia & Middle East", ["UAE", "Singapore", "Malaysia", "Mauritius"]],
];

export function StudyAbroadPage() {
  useMeta("Study Abroad — Explore 17 Destinations | Harwell Global Education",
    "Explore study abroad destinations across Europe, North America, Oceania and Asia — with guidance from profile assessment to pre-departure.");
  const [countries] = useCollection("countries");
  return (
    <div data-testid="study-abroad-page">
      <LightHero overline="Study Abroad" title="EXPLORE THE WORLD'S" accent="CLASSROOMS."
        sub="Seventeen destinations across four regions — compared honestly, guided personally." />
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {REGIONS.map(([region, names]) => {
            const list = countries.filter((c) => names.includes(c.name));
            if (list.length === 0) return null;
            return (
              <div key={region} className="mb-14">
                <h2 className="text-xl sm:text-2xl font-extrabold text-[#172033] tracking-tight mb-6">{region}</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {list.map((c, i) => <CountryCard key={c.id} c={c} i={i} />)}
                </div>
              </div>
            );
          })}
          <Disclaimer />
          <CTABanner />
        </div>
      </section>
    </div>
  );
}

/* ---------------- LEGAL PAGES ---------------- */

const LEGAL_DOCS = {
  privacy: {
    title: "PRIVACY POLICY",
    body: "Harwell Global Education respects your privacy.\n\n## What we collect\n\nInformation you submit through enquiry and assessment forms — name, contact details, academic background and study preferences.\n\n## How we use it\n\nTo respond to your enquiry, provide counselling, and share relevant study options. We do not sell your personal information.\n\n## Storage & security\n\nEnquiry data is stored securely and accessed only by the Harwell counselling team.\n\n## Your choices\n\nYou may request correction or deletion of your information at any time by emailing Contact@harwellglobal.com.",
  },
  terms: {
    title: "TERMS & CONDITIONS",
    body: "By using this website you accept these terms.\n\n## Nature of our services\n\nHarwell Global Education provides international education counselling and guidance. We do not make admission, visa, scholarship or employment decisions — these rest with universities, government authorities and other official bodies.\n\n## Information accuracy\n\nEducation, tuition, visa and policy information can change. Content on this website is for general guidance and should be verified with official sources.\n\n## Intellectual property\n\nWebsite content and branding belong to Harwell Global Education and may not be reproduced without permission.",
  },
  cookies: {
    title: "COOKIE POLICY",
    body: "This website uses minimal cookies.\n\n## What we use\n\nEssential cookies for site functionality and, where analytics tools are enabled, anonymised usage measurement.\n\n## Your control\n\nYou can control or delete cookies through your browser settings at any time.",
  },
  disclaimer: {
    title: "DISCLAIMER",
    body: "The information on this website is provided for general educational guidance.\n\n## No guarantees\n\nHarwell Global Education does not guarantee admission, scholarships, visas, employment or residency outcomes. All such decisions are made by the relevant universities, government and immigration authorities.\n\n## Changing information\n\nRequirements, fees, intakes, visa rules and university policies can change. Students should verify current information with the relevant university and official government/immigration sources.\n\n## External links\n\nLinks to official university and government websites are provided for convenience; we are not responsible for their content.",
  },
};

export function LegalPage({ type }) {
  const doc = LEGAL_DOCS[type] || LEGAL_DOCS.privacy;
  useMeta(`${doc.title.charAt(0) + doc.title.slice(1).toLowerCase()} | Harwell Global Education`, doc.body.split("\n")[0]);
  return (
    <div data-testid={`legal-page-${type}`}>
      <LightHero overline="Legal" title={doc.title} />
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <ContentBlocks text={doc.body} />
          <p className="text-xs text-slate-400 mt-8">Last updated: August 2026</p>
        </div>
      </section>
    </div>
  );
}
